/**
 * Authentication Response DTOs
 * What backend RETURNS to frontend
 * Contains safe fields only (no sensitive data)
 *
 * `UserPreferences`, `UserRole` and `UserResponse` are INFERRED from the schemas below (the shell screen and the
 * authentication routes read the same schema): the shape is written once, and it says what the server SENDS:
 * `id` is a uuid, `email` an e-mail, the dates ISO date-times, `lastLoginAt` is OMITTED (never null) for a user who has
 * not signed in yet, and `role` is any non-empty text: a user can hold a custom role of their organisation
 * (`users.role` is text), and whether a role exists is decided by the services against the database, not by a
 * response schema. `UserRole` stays as the four built-in roles, for whoever compares with them.
 */

import { z } from 'zod/v4'

// The preferences as the server sends them; the type is inferred (domain types are backend-only)
export const userPreferencesResponseSchema = z.object({
  language: z.string(),
  timezone: z.string(),
  dateFormat: z.string(),
  numberFormat: z.string(),
  currency: z.string(),
  // 'auto' is the LEGACY alias of 'system' (old THEME_OPTIONS entry). The
  // backend may still emit it for users with old persisted preferences; the
  // frontend normalizes 'auto' → 'system' on read (auth user.transformer).
  theme: z.enum(['light', 'dark', 'auto', 'system']),
  emailNotifications: z.boolean(),
  pushNotifications: z.boolean(),
  // twoFactorEnabled removed for MVP
})
export type UserPreferences = z.infer<typeof userPreferencesResponseSchema>

// User roles in the system
// The four built-in roles (not every role a user can hold: see `userResponseSchema.role`)
export const userRoleSchema = z.enum(['super_admin', 'org_admin', 'user', 'viewer'])
export type UserRole = z.infer<typeof userRoleSchema>

// Safe user data for frontend consumption
//
// EXCLUDED: tenantId (internal)
// EXCLUDED: passwordHash (sensitive)
// EXCLUDED: failedLoginAttempts, lockedUntil (security)
// EXCLUDED: verification tokens (sensitive)
// EXCLUDED: createdBy, lastModifiedBy (audit - internal)
export const userResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(), // Non-null: the transformer converts null to an empty string
  lastName: z.string(),
  role: z.string().min(1), // A built-in role or a custom role of the organisation
  status: z.enum(['active', 'inactive', 'suspended', 'pending']),
  emailVerified: z.boolean(),
  lastLoginAt: z.string().datetime().optional(), // Omitted (not null) when the user never signed in
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),

  // Extended profile fields
  avatar: z.string().optional(),
  company: z.string().optional(),
  timezone: z.string().optional(),
  phone: z.string().optional(),
  bio: z.string().optional(),
  preferences: userPreferencesResponseSchema.optional(),
})
export type UserResponse = z.infer<typeof userResponseSchema>

// Login response with tokens and user data
export interface LoginResponse {
  user: UserResponse
  tokens: {
    accessToken: string
    tokenType: string
    expiresIn: number
  }
  // refreshToken is sent as httpOnly cookie, not in response body
}

// Registration response
export interface RegisterResponse {
  user: UserResponse
  message: string
  // Tokens not included - user needs to login after registration
}

// Password reset response
export interface PasswordResetResponse {
  message: string
  // No sensitive data returned
}

// Password reset confirmation response
export interface PasswordResetConfirmResponse {
  message: string
  // User needs to login again after password reset
}

// Session info response
export interface SessionResponse {
  id: string
  userAgent?: string
  ipAddress?: string
  lastUsedAt: string
  createdAt: string
  isActive: boolean
  // EXCLUDED: refreshToken (sensitive)
  // EXCLUDED: tenantId, userId (internal)
}
